import { type ReactNode } from "react";
import { Pencil, Plus } from "lucide-react";

import { getText } from "shared/lib";
import { type TreeDescriptor } from "../lib";

import { Button } from "shared/components";
import { TreeItem } from "./TreeItem";

export interface TreeListProps {
  addButtonLabel?: string;
  emptyText?: string;
  hideDelete?: boolean;
  isOtherTreeActivity?: boolean;
  onClick?: (treeDescriptor: TreeDescriptor) => void;
  onClickDuplicate?: (treeDescriptor: TreeDescriptor) => void;
  onClickDelete?: (treeDescriptor: TreeDescriptor) => void;
  onClickAddTree?: () => void;
  selectedIndex?: number;
  subTitle?: string;
  title?: ReactNode;
  treeDescriptors: Array<TreeDescriptor>;
  treesHaveBeenDeleted?: boolean;
}

export function TreeList({
  addButtonLabel,
  emptyText,
  isOtherTreeActivity,
  hideDelete,
  onClick,
  onClickDuplicate,
  onClickDelete,
  onClickAddTree,
  selectedIndex,
  subTitle,
  title,
  treeDescriptors,
  treesHaveBeenDeleted,
}: TreeListProps) {
  return (
    <div className="flex shrink flex-col gap-2">
      <div>
        <h2 className="text-textPrimary text-bodylg flex items-center gap-1">
          {title}
        </h2>

        {subTitle ? (
          <p className="text-bodysm text-textSubdued">{subTitle}</p>
        ) : null}
      </div>

      {treeDescriptors.length > 0 ? (
        <ul className="[&>li:not(:last-child)]:mb-2">
          {treeDescriptors.map((treeDescriptor, index) => (
            <li key={treeDescriptor.treeId}>
              <TreeItem
                hideDelete={hideDelete}
                onClick={() => onClick?.(treeDescriptor)}
                onClickDelete={() => onClickDelete?.(treeDescriptor)}
                onClickDuplicate={() => onClickDuplicate?.(treeDescriptor)}
                selected={index === selectedIndex}
                treeDescriptor={treeDescriptor}
              />
            </li>
          ))}
        </ul>
      ) : null}

      {treeDescriptors.length === 0 && treesHaveBeenDeleted && emptyText ? (
        <p className="text-bodysm text-textDisabled">{emptyText}</p>
      ) : null}

      <Button
        className="text-bodymd h-min w-min gap-1 px-0 py-1 underline"
        onClick={onClickAddTree}
        variant="link"
      >
        <Plus className="min-h-5 min-w-5" />
        {addButtonLabel || getText("Add tree")}
      </Button>
    </div>
  );
}
